import { SerializedStyles, css, keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { filter, pipe } from "callbag-basics";
import subscribe from "callbag-subscribe";
import { isEqual } from "lodash";
import { FC, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { Add, AiColored, AiOutlined, Info } from "@livechat/design-system-icons";
import {
  Badge,
  Button,
  DesignToken,
  Icon,
  RadiusToken,
  SpacingToken,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import { TextHighlight } from "components/text-highlight/TextHighlight";
import { HelpCenter } from "constants/help-center";
import { actions$ } from "store/action-stream";
import { RequestAction } from "store/entities/actions";
import { selectTags } from "store/entities/tags";
import { TICKET } from "store/entities/tickets/actions";
import { IUpdateTicketTagsPayload } from "store/entities/tickets/interfaces";
import { IActionWithPayload } from "store/helper";
import { selectSuggestedTagIds } from "store/views/tickets/selectors";
import { withVar } from "themes/helpers";

interface IProps {
  onTagAdd: (tagId: string) => void;
}

export const AiTagSuggestions: FC<IProps> = ({ onTagAdd }) => {
  const tagsByIds = useSelector(selectTags);
  const suggestedTagIds = useSelector(selectSuggestedTagIds);
  const isLoading = !suggestedTagIds;

  const containerRef = useRef<HTMLDivElement>(null);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [visibleTagIndices, setVisibleTagIndices] = useState<number[]>([]);
  const [addingTagId, setAddingTagId] = useState<string | null>(null);

  const displayBadge = !expanded && suggestedTagIds && suggestedTagIds.length > 0 && hiddenCount > 0;
  const displaySearchingState = isLoading;
  const displayEmptyState = suggestedTagIds && suggestedTagIds.length === 0 && !isLoading;
  const displayColoredIcon = (suggestedTagIds && suggestedTagIds.length > 0) || isLoading;
  const displayEmptyStateTooltipIcon = suggestedTagIds && suggestedTagIds.length === 0 && !isLoading;

  const handleShowMore = (): void => {
    setExpanded(true);
  };

  const handleTagClick = (tagId: string): void => {
    onTagAdd(tagId);
    setAddingTagId(tagId);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const visibleIndices = entries
          .filter(entry => entry.isIntersecting)
          .map(entry => Number(entry.target.getAttribute("data-index")));

        const newHiddenCount = (suggestedTagIds ? suggestedTagIds.length : 0) - visibleIndices.length;

        if (!isEqual(visibleIndices, visibleTagIndices) || hiddenCount !== newHiddenCount) {
          setVisibleTagIndices(visibleIndices);
          setHiddenCount(newHiddenCount);
        }
      },
      {
        root: containerRef.current,
        threshold: 0.1,
      }
    );

    const container = containerRef.current;
    if (container) {
      const children = Array.from(container.children);
      children.forEach(child => {
        observer.observe(child);
      });
    }

    return (): void => {
      observer.disconnect();
    };
  }, [suggestedTagIds, visibleTagIndices, hiddenCount]);

  useEffect(() => {
    const unsubscribe = pipe(
      actions$,
      filter(
        (action: IActionWithPayload<string, IUpdateTicketTagsPayload>) =>
          action.type === TICKET.ADD_TAGS[RequestAction.SUCCESS] ||
          action.type === TICKET.ADD_TAGS[RequestAction.FAILURE]
      ),
      subscribe((action: IActionWithPayload<string, IUpdateTicketTagsPayload>) => {
        if (
          action.type === TICKET.ADD_TAGS[RequestAction.SUCCESS] ||
          action.type === TICKET.ADD_TAGS[RequestAction.FAILURE]
        ) {
          setAddingTagId(null);
        }
      })
    );

    return (): void => {
      unsubscribe();
    };
  }, []);

  return (
    <Container suggestions={suggestedTagIds || []} loadingSuggestions={isLoading}>
      <AiIconContainer>
        <AiIcon
          source={displayColoredIcon ? AiColored : AiOutlined}
          customColor={withVar(DesignToken.ContentBasicSecondary)}
          animationEnabled={displayColoredIcon}
        />
      </AiIconContainer>
      <TagContainer expanded={expanded} ref={containerRef}>
        {displayEmptyState && <EmptyStateText>No suggested tags found</EmptyStateText>}
        {displaySearchingState && (
          <TextHighlight
            text="Searching suggested tags..."
            baseColor={withVar(DesignToken.ContentBasicDisabled)}
            highlightColor={withVar(DesignToken.ContentLockedWhite)}
            speed={0.05}
            css={{ marginLeft: withVar(SpacingToken.Spacing1) }}
          />
        )}
        {suggestedTagIds?.map((tagId, index) => (
          <ButtonWithoutBreaks
            key={tagId}
            id={tagId}
            size="xcompact"
            icon={<Icon source={Add} />}
            kind="float"
            onClick={() => handleTagClick(tagId)}
            loading={addingTagId === tagId}
            disabled={addingTagId === tagId}
            data-index={index}
            data-testid={`tag-${tagId}`}
            index={index}
          >
            {tagsByIds[tagId].name}
          </ButtonWithoutBreaks>
        ))}
      </TagContainer>
      {displayBadge && (
        <ShowMoreBadge kind="secondary" size="compact" type="counter" count={hiddenCount} onClick={handleShowMore} />
      )}
      {displayEmptyStateTooltipIcon && (
        <LearnMoreTooltipContainer>
          <LearnMoreTooltip
            kind="invert"
            placement="top-end"
            triggerRenderer={() => (
              <EmptyStateTooltipIcon
                source={Info}
                size="small"
                customColor={withVar(DesignToken.ContentBasicSecondary)}
              />
            )}
          >
            Suggested Tags are AI-powered recommendations to help you categorize your tickets quickly and accurately.{" "}
            <Button kind="link-inverted" size="compact" target="_blank" href={HelpCenter.TAG_SUGGESTIONS}>
              Learn more
            </Button>
          </LearnMoreTooltip>
        </LearnMoreTooltipContainer>
      )}
    </Container>
  );
};

const Container = styled.div<{ suggestions: string[]; loadingSuggestions: boolean }>`
  display: flex;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing1)};
  margin-bottom: ${withVar(SpacingToken.Spacing2)};
  border-radius: ${withVar(RadiusToken.Radius3)};
  padding: ${(props): string =>
    props.suggestions.length > 0
      ? `${withVar(SpacingToken.Spacing1)} ${withVar(SpacingToken.Spacing2)}`
      : `${withVar(SpacingToken.Spacing1)} 0`};
  transition: padding 0.4s ease-in-out;
  z-index: 0;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: ${withVar(RadiusToken.Radius3)};
    background: ${withVar(DesignToken.SurfaceGradient11)};
    z-index: -1;
    transition: opacity 0.4s ease-in-out;
    opacity: ${(props): number => (props.suggestions.length > 0 ? 1 : 0)};
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: ${withVar(RadiusToken.Radius3)};
    background: transparent;
    z-index: -2;
  }
`;

const TagContainer = styled.div<{ expanded: boolean }>`
  transition: max-height 0.4s ease-in;
  display: flex;
  overflow: hidden;
  flex-wrap: wrap;
  max-height: ${(props): string => (props.expanded ? "136px" : "32px")};
  max-width: ${(props): string => (props.expanded ? "100%" : "calc(100% - 48px)")};
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const ShowMoreBadge = styled(Badge)`
  cursor: pointer;
  margin-left: auto;
  opacity: 0;
  animation: ${slideIn} 0.5s ease-out forwards;
`;

const EmptyStateTooltipIcon = styled(Icon)`
  margin-left: auto;
`;

const ButtonWithoutBreaks = styled(Button)<{ index: number }>`
  white-space: nowrap;
  margin: ${withVar(SpacingToken.Spacing1)};
  overflow: hidden;

  opacity: 0;
  transform: translateY(-20px);
  animation: ${slideIn} 0.5s ease-out forwards;
  animation-delay: ${(props): number => props.index * 0.2 + 0.3}s;
`;

const AiIconContainer = styled.div`
  height: 32px;
  display: flex;
  align-items: center;
  align-self: flex-start;
`;

const AiIcon = styled(Icon)<{ animationEnabled: boolean }>`
  ${(props): SerializedStyles =>
    props.animationEnabled
      ? css`
          animation: ${fadeIn} 0.4s ease-out forwards;
        `
      : css``}
`;

const EmptyStateText = styled(Text)`
  margin-left: ${withVar(SpacingToken.Spacing1)};
  color: ${withVar(DesignToken.ContentBasicDisabled)};
  animation: ${fadeIn} 0.4s ease-out forwards;
`;

const LearnMoreTooltip = styled(Tooltip)`
  max-width: 240px;
`;

const LearnMoreTooltipContainer = styled.div`
  margin-left: auto;
`;
